const NETWORK_ERROR_PATTERN =
  /load failed|failed to fetch|networkerror|network request failed/i;

/**
 * Browser fetch transport failures (offline, connection dropped, request torn
 * down on navigation). These reject with no stack, so they are unactionable as
 * `error`-level items — Safari reports them as `TypeError: Load failed`, Chrome
 * as `TypeError: Failed to fetch`.
 */
export const isNetworkError = (error: Error): boolean =>
  error.name === 'TypeError' && NETWORK_ERROR_PATTERN.test(error.message);

const hasAbortName = (value: unknown): boolean => {
  if (value instanceof Error) {
    return value.name === 'AbortError';
  }
  // Native fetch aborts reject with a DOMException, which is not `instanceof
  // Error` — `toError` wraps it into a generic `Error` and the original survives
  // only on `cause`.
  if (typeof DOMException !== 'undefined' && value instanceof DOMException) {
    return value.name === 'AbortError';
  }
  return false;
};

/**
 * Requests cancelled in flight (e.g. TanStack Query aborting on unmount). Never
 * a real failure. Matched by name only — a message mentioning "aborted" is not
 * enough, since real backend errors ("Transaction aborted") use that wording.
 */
export const isAbortError = (error: Error): boolean =>
  hasAbortName(error) || hasAbortName(error.cause);
